<template>
  <v-dialog
    v-model="createBulkApplicationDialog"
    max-width="640"
    content-class="bulk-dialog"
    @click:outside="toggleCreateBulkApplicationDialog(false)"
  >
    <div class="bulk-card">
      <!-- Header -->
      <div class="bulk-card__header">
        <div class="bulk-card__header-left">
          <v-icon size="20" color="#fff" class="mr-2">mdi-file-upload-outline</v-icon>
          <span class="bulk-card__header-title">Bulk Upload Applications</span>
        </div>
        <div class="bulk-card__header-right">
          <v-menu offset-y :rounded="'lg'" transition="slide-y-transition" :max-height="'300'">
            <template v-slot:activator="{ on, attrs }">
              <button class="bulk-card__menu-btn" v-bind="attrs" v-on="on">
                <v-icon size="18" color="rgba(255,255,255,0.8)">mdi-dots-vertical</v-icon>
              </button>
            </template>
            <div class="bulk-dropdown">
              <button
                v-for="(action, index) in bulkActions"
                :key="`action_${index}`"
                class="bulk-dropdown__item"
                @click="executeAction(action.value)"
              >
                <v-icon size="16" color="var(--app-text-muted)" class="mr-2">mdi-download</v-icon>
                {{ action.text }}
              </button>
            </div>
          </v-menu>
          <button class="bulk-card__close-btn" @click="toggleCreateBulkApplicationDialog(false)">
            <v-icon size="16" color="rgba(255,255,255,0.8)">mdi-close</v-icon>
          </button>
        </div>
      </div>

      <!-- Body -->
      <div class="bulk-card__body">
        <!-- Step 1 -->
        <div class="bulk-step">
          <div class="bulk-step__badge">1</div>
          <div class="bulk-step__content">
            <h4 class="bulk-step__title">Download Template</h4>
            <p class="bulk-step__desc">Download sample excel template & follow the instruction to upload bulk applications.
              <span class="bulk-step__hint">(use menu at upper right corner)</span>
            </p>
          </div>
        </div>

        <!-- Step 2 -->
        <div class="bulk-step">
          <div class="bulk-step__badge">2</div>
          <div class="bulk-step__content">
            <h4 class="bulk-step__title">Select Event</h4>
            <p class="bulk-step__desc">Click to copy, then paste into <strong>'Workshop / Event Session'</strong> column:</p>
            <div class="bulk-event-list">
              <button
                v-for="(item, key) in eventLists"
                :key="`event_${key}`"
                class="bulk-event-chip"
                @click="copyContentFromChild(item.text)"
              >
                <v-icon size="14" color="#0369a1" class="mr-1">mdi-content-copy</v-icon>
                {{ item.text }}
              </button>
            </div>
          </div>
        </div>

        <!-- Step 3 -->
        <div class="bulk-step">
          <div class="bulk-step__badge">3</div>
          <div class="bulk-step__content">
            <h4 class="bulk-step__title">Upload Excel</h4>
            <p class="bulk-step__desc">After filling up the excel, upload below:</p>
            <v-file-input
              :prepend-icon="null"
              prepend-inner-icon="mdi-paperclip"
              dense
              outlined
              small-chips
              placeholder="Choose excel file..."
              accept=".xlsx, .xls"
              v-model="formData.bulkApplicationAttachment"
              :error-messages="errorField.bulkApplicationAttachment"
              :truncate-length="1000"
              class="bulk-file-input"
              hide-details="auto"
              @change="readExcelDataFromChild()"
            ></v-file-input>
          </div>
        </div>

        <!-- Step 4 -->
        <div class="bulk-step">
          <div class="bulk-step__badge">4</div>
          <div class="bulk-step__content">
            <h4 class="bulk-step__title">Bank Account Attachments</h4>
            <p class="bulk-step__desc">Upload bank account details for each company:</p>
            <div
              v-for="(data, index) in excelDatas"
              :key="`excel_data_${index}`"
              v-show="!errorDetected"
            >
              <v-file-input
                v-if="data.isPaymentMade"
                :prepend-icon="null"
                prepend-inner-icon="mdi-paperclip"
                dense
                outlined
                small-chips
                accept=".pdf, image/*"
                :placeholder="`Group ${data.group} - ${data.companyName}`"
                v-model="formData.ssRefundBankAccAtch[index]"
                :error-messages="errorField.ssRefundBankAccAtch"
                :truncate-length="1000"
                class="bulk-file-input mt-2"
                hide-details="auto"
                @change="
                  uploadSsBankAtchFromChild({
                    index,
                    grp: data.group,
                  })
                "
              ></v-file-input>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="bulk-card__footer">
        <button class="bulk-card__btn bulk-card__btn--cancel" @click="toggleCreateBulkApplicationDialog(false)">
          Cancel
        </button>
        <button class="bulk-card__btn bulk-card__btn--confirm" @click="importDataFromChild()">
          <v-icon size="16" color="#fff" class="mr-1">mdi-upload</v-icon>
          Import
        </button>
      </div>
    </div>
  </v-dialog>
</template>

<script>
export default {
  props: {
    createBulkApplicationDialog: Boolean,
    hrdcThemeColor: String,
    bulkActions: Array,
    eventLists: Array,
    serviceKey: String,
    formData: Object,
    errorField: Object,
    excelDatas: Array,
    errorDetected: Boolean,
  },
  emits: [
    "toggleCreateBulkApplicationDialog",
    "executeBulkAction",
    "copyContent",
    "readExcelData",
    "uploadSsBankAtch",
    "importData",
  ],
  methods: {
    toggleCreateBulkApplicationDialog(val) {
      this.$emit("toggleCreateBulkApplicationDialog", val);
    },
    executeAction(action) {
      this.$emit("executeBulkAction", action);
    },
    copyContentFromChild(item) {
      this.$emit("copyContent", item);
    },
    readExcelDataFromChild() {
      this.$emit("readExcelData");
    },
    uploadSsBankAtchFromChild(data) {
      this.$emit("uploadSsBankAtch", data);
    },
    importDataFromChild() {
      this.$emit("importData");
    },
  },
};
</script>

<style>
/* >>> auto-dark: dark companions for the light rules below. Colours here are
   token references only (--app-*, defined in src/plugins/theme-dark.css) — do
   NOT paste literal hexes back in, and do not regenerate this block from the
   light rules: that is what made the palette drift across 226 files before.
   Hand-written `.dark-mode` rules later in this file still override these. <<< */

.dark-mode .bulk-card {
  background: var(--app-card);
}

.dark-mode .bulk-dropdown {
  background: var(--app-card);
}

.dark-mode .bulk-dropdown__item {
  color: var(--app-text);
}

.dark-mode .bulk-dropdown__item:hover {
  background: var(--app-accent-soft);
}

.dark-mode .bulk-card__body::-webkit-scrollbar-thumb {
  background: var(--app-elevated);
}

.dark-mode .bulk-step {
  border-bottom: 1px solid var(--app-border);
}

.dark-mode .bulk-step__title {
  color: var(--app-text);
}

.dark-mode .bulk-step__desc {
  color: var(--app-text-muted);
}

.dark-mode .bulk-event-chip {
  background: var(--app-accent-soft);
}

.dark-mode .bulk-event-chip:hover {
  background: var(--app-accent-soft);
}

.dark-mode .bulk-file-input.v-text-field--outlined fieldset {
  border-color: var(--app-border) !important;
}

.dark-mode .bulk-file-input.v-text-field--outlined:hover fieldset {
  border-color: var(--app-border-strong) !important;
}

.dark-mode .bulk-card__footer {
  border-top: 1px solid var(--app-border);
}

.dark-mode .bulk-card__btn--cancel {
  background: var(--app-card);
  color: var(--app-text-muted);
  border: 1.5px solid var(--app-border-strong);
}

.dark-mode .bulk-card__btn--cancel:hover {
  background: var(--app-surface);
  border-color: var(--app-border-strong);
}

/* >>> end auto-dark <<< */

.bulk-dialog {
  border-radius: 20px !important;
  overflow: hidden;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12) !important;
}

.bulk-card {
  background: #fff;
  border-radius: 20px;
  overflow: hidden;
  font-family: 'Lato', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif;
}

/* Header */
.bulk-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  background: linear-gradient(135deg, #0ea5e9, #38bdf8);
}

.bulk-card__header-left {
  display: flex;
  align-items: center;
}

.bulk-card__header-title {
  font-size: 0.95rem;
  font-weight: 700;
  color: #fff;
}

.bulk-card__header-right {
  display: flex;
  align-items: center;
  gap: 6px;
}

.bulk-card__menu-btn,
.bulk-card__close-btn {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  border: none;
  background: rgba(255, 255, 255, 0.15);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;
}

.bulk-card__menu-btn:hover,
.bulk-card__close-btn:hover {
  background: rgba(255, 255, 255, 0.3);
}

/* Dropdown */
.bulk-dropdown {
  background: #fff;
  padding: 4px;
  min-width: 260px;
  border-radius: 12px;
}

.bulk-dropdown__item {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 10px 14px;
  border: none;
  background: transparent;
  border-radius: 8px;
  font-family: 'Lato', sans-serif;
  font-size: 0.82rem;
  font-weight: 600;
  color: #334155;
  cursor: pointer;
  transition: all 0.12s ease;
  text-align: left;
}

.bulk-dropdown__item:hover {
  background: #f0f9ff;
  color: #0369a1;
}

/* Body */
.bulk-card__body {
  padding: 24px;
  max-height: 60vh;
  overflow-y: auto;
}

.bulk-card__body::-webkit-scrollbar {
  width: 5px;
}

.bulk-card__body::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 10px;
}

/* Steps */
.bulk-step {
  display: flex;
  gap: 14px;
  padding-bottom: 20px;
  margin-bottom: 20px;
  border-bottom: 1px solid #e2e8f0;
}

.bulk-step:last-child {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

.bulk-step__badge {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: linear-gradient(135deg, #0ea5e9, #38bdf8);
  color: #fff;
  font-size: 0.75rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 2px;
}

.bulk-step__content {
  flex: 1;
  min-width: 0;
}

.bulk-step__title {
  font-size: 0.88rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 4px;
}

.bulk-step__desc {
  font-size: 0.82rem;
  color: #64748b;
  margin: 0;
  line-height: 1.5;
}

.bulk-step__hint {
  color: #dc2626;
  font-size: 0.75rem;
  font-weight: 600;
}

/* Event chips */
.bulk-event-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 10px;
}

.bulk-event-chip {
  display: inline-flex;
  align-items: center;
  padding: 8px 14px;
  background: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: 8px;
  font-family: 'Lato', sans-serif;
  font-size: 0.78rem;
  font-weight: 600;
  color: #0369a1;
  cursor: pointer;
  transition: all 0.15s ease;
  text-align: left;
  word-break: break-word;
}

.bulk-event-chip:hover {
  background: #e0f2fe;
  border-color: #7dd3fc;
}

/* File input */
.bulk-file-input.v-text-field--outlined fieldset {
  border-color: #e2e8f0 !important;
  border-radius: 10px !important;
}

.bulk-file-input.v-text-field--outlined:hover fieldset {
  border-color: #cbd5e1 !important;
}

.bulk-file-input.v-input--is-focused fieldset {
  border-color: #0ea5e9 !important;
  border-width: 2px !important;
}

.bulk-file-input .v-input__slot {
  min-height: 42px !important;
}

.bulk-file-input input,
.bulk-file-input .v-file-input__text {
  font-family: 'Lato', sans-serif !important;
  font-size: 0.82rem !important;
  font-weight: 600 !important;
}

/* Footer */
.bulk-card__footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 24px;
  border-top: 1px solid #e2e8f0;
}

.bulk-card__btn {
  padding: 10px 22px;
  border-radius: 10px;
  font-size: 0.85rem;
  font-weight: 600;
  font-family: 'Lato', sans-serif;
  cursor: pointer;
  transition: all 0.15s ease;
  display: inline-flex;
  align-items: center;
}

.bulk-card__btn--cancel {
  background: #fff;
  color: #64748b;
  border: 1.5px solid #d1d5db;
}

.bulk-card__btn--cancel:hover {
  background: #f8fafc;
  border-color: #94a3b8;
}

.bulk-card__btn--confirm {
  background: #0ea5e9;
  color: #fff;
  border: none;
}

.bulk-card__btn--confirm:hover {
  background: #0284c7;
  box-shadow: 0 4px 12px rgba(14, 165, 233, 0.3);
}

/* Dark mode */
.dark-mode .bulk-card { background: var(--app-card); }
.dark-mode .bulk-card__body { color: var(--app-text); }
.dark-mode .bulk-step { border-color: var(--app-border); }
.dark-mode .bulk-step__title { color: var(--app-text); }
.dark-mode .bulk-step__desc { color: var(--app-text-muted); }
.dark-mode .bulk-event-chip { background: var(--app-accent-soft); border-color: var(--app-accent); color: var(--app-accent-strong); }
.dark-mode .bulk-card__footer { border-color: var(--app-border); }
.dark-mode .bulk-file-input.v-text-field--outlined fieldset { border-color: var(--app-border) !important; }
</style>
